import React, { useEffect, useState } from 'react'
import PageLoader from '../../shared/PageLoader'
import { useAppDispatch, useAppSelector } from '../../../redux/hook';
import {
  Box, Button, Checkbox, Modal, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TableSortLabel, Typography, IconButton
} from '@mui/material';
import { FaDownload } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { useExportEmployeesAttendance } from '../../../backend/exports';
import { ERROR_MESSAGES } from '../../../utils/constants';
import { toast } from 'react-toastify';
import { bulkDeleteEmployeeAttendance, getEmployeesAttendance } from '../../../redux/features/employeeMaster.slice';
import { resetBulkDeleteHolidaysDetails, resetUploadHolidayDetails } from '../../../redux/features/holidayList.slice';
import EmployeeUploadFilters from '../../common/EmployeeUploadFilters';
import { useDefaultBrandingOnly } from '../../../contexts/AssociateCompanyContext';
import AdditionalFiltersInputUploads from '../../common/AdditionalFiltersInputUploads';
import {
  useEmployeeUploadFilters,
  useEmployeeUploadSearchConfig,
  useEmployeeUploadData,
  useEmployeeUploadCompanyData,
  useEmployeeUploadCompanyAutoSelect
} from '../../../hooks/useEmployeeUploads';
import {
  getEmployeeUploadCompanyNameById,
  getEmployeeUploadAssocCompanyNameById,
  getEmployeeUploadLocationById,
  buildEmployeeUploadQueryPayload
} from '../../../utils/employeeUploads.utils';

const style = {
  position: 'absolute' as 'absolute',
  top: '25%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  bgcolor: 'background.paper',
  boxShadow: 24,
};

const EmployeeAttendanceUpload = () => {
  const dispatch = useAppDispatch();

  // Redux selectors
  const employeesAttendanceDetails = useAppSelector((state) => state.employeeMaster.employeesAttendanceDetails);
  const bulkDeleteEmployeesAttendanceDetails = useAppSelector((state) => state.employeeMaster.bulkDeleteEmployeeAttendance);
  const uploadHolidayDetails = useAppSelector((state) => state.holidayList.uploadHolidayDetails);

  // Use custom hooks
  const { filters, updateFilter, updateMultipleFilters } = useEmployeeUploadFilters();
  const { searchConfig, updateSearchConfig } = useEmployeeUploadSearchConfig();
  const { fetchData, fetchWithSearch } = useEmployeeUploadData({
    monthFieldName: 'wage_month_eq',
    searchFieldName: 'employee_code_cont'
  });
  const {
    companies,
    associateCompanies,
    locations,
    companiesLoading,
    associateCompaniesLoading,
    fetchCompanies,
    fetchAssociateCompanies,
    fetchLocations
  } = useEmployeeUploadCompanyData();

  // This is a Super Admin / Input Module page, not scoped to one client —
  // white-label branding never applies here. Still auto-select Company/
  // Associate Company (no "All") for a consistent filter UX, but pass
  // bindLogo:false so it never touches the sidenav logo, and force the
  // default ezyCOMP logo for as long as this page stays mounted.
  useDefaultBrandingOnly();
  useEmployeeUploadCompanyAutoSelect(
    filters,
    updateFilter,
    companies,
    companiesLoading,
    associateCompanies,
    associateCompaniesLoading,
    false,
  );

  // Local state for UI components
  const [companyName, setCompanyName] = useState('');
  const [associate_company_name, setAssociateCompanyName] = useState('');
  const [locationName, setLocationName] = useState('');
  const [companyLocationMappingId, setCompanyLocationMappingId] = useState('');
  const [showAdditionalFiltersModal, setShowAdditionalFiltersModal] = useState(false);
  const [selectedAdditionalFilters, setSelectedAdditionalFilters] = useState<any>({});

  const [selectedAttendance, setSelectedAttendance] = useState<any>([]);
  const [openBulkDeleteModal, setOpenBulkDeleteModal] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [openUploadModal, setOpenUploadModal] = useState(false);

  const employeesAttendance = employeesAttendanceDetails.data.items || [];
  const employeesAttendanceCount = employeesAttendanceDetails.data.meta.total_count;

  const { exportRecords, exporting } = useExportEmployeesAttendance(() => {
    toast.success('Export has been sent to your registered email.');       
  }, () => {
    toast.error(ERROR_MESSAGES.DEFAULT);                                                                                                                                                                    
  });

  const loading = exporting || uploadHolidayDetails.status === 'loading' || bulkDeleteEmployeesAttendanceDetails.status === 'loading' || employeesAttendanceDetails.status === 'loading';

  // Data fetching effects
  useEffect(() => {
    // Data is fetched by the filter effect below, only once a company is set.
    fetchCompanies();
  }, []);

  // Fetch data when filters change (including additional filters and year/month)
  useEffect(() => {
    // Don't hit the API until the company is resolved — an unscoped call can
    // resolve late and overwrite the real results.
    if (filters.company) {
      updateSearchConfig({ page: 0 });
      fetchData(getEmployeesAttendance, filters, { ...searchConfig, page: 0 });
    }
  }, [filters.establishmentType, filters.company, filters.associateCompany, filters.location, filters.year, filters.month,
      filters.employer, filters.employerLocation, filters.subContractor, filters.subContractorLocation]);

  useEffect(() => {
    if (filters.company) {
      fetchAssociateCompanies(filters.company);
      const selectedCompanyName = getEmployeeUploadCompanyNameById(filters.company, companies);
      setCompanyName(selectedCompanyName);
    }
  }, [filters.company, companies]);

  useEffect(() => {
    if (filters.associateCompany) {
      fetchLocations(filters.associateCompany);
      const selectedCompanyName = getEmployeeUploadAssocCompanyNameById(filters.associateCompany, associateCompanies);
      setAssociateCompanyName(selectedCompanyName);
    }
  }, [filters.associateCompany, associateCompanies]);

  useEffect(() => {
    if (filters.location) {
      const selectedLocationId = filters.location.split('^')[0];
      const selectedLocationName = getEmployeeUploadLocationById(selectedLocationId, locations);
      setLocationName(selectedLocationName);
      const tempLocation = locations.find((loc: any) => loc.location_id === selectedLocationId) || {};
      setCompanyLocationMappingId(tempLocation.id || '');
    }
  }, [filters.location, locations]);

  // Clear additional filters when parent filters change
  useEffect(() => {
    setSelectedAdditionalFilters({});
  }, [filters.establishmentType, filters.company, filters.associateCompany, filters.location]);

  // Handler functions
  const handleSearch = () => {
    updateSearchConfig({ page: 0 });
    fetchData(getEmployeesAttendance, filters, { ...searchConfig, page: 0 });
  };

  const handleClearSearch = () => {
    updateSearchConfig({ searchInput: '' });
    fetchData(getEmployeesAttendance, filters, { ...searchConfig, searchInput: '' });
  };

  const handleSort = (sortField: string) => {
    const newSortDirection = searchConfig.activeSort === sortField && searchConfig.sortType === 'asc' ? 'desc' : 'asc';
    updateSearchConfig({
      activeSort: sortField,
      sortType: newSortDirection
    });
    fetchData(getEmployeesAttendance, filters, searchConfig, {}, sortField, newSortDirection);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    updateSearchConfig({ page: newPage });
    fetchData(getEmployeesAttendance, filters, { ...searchConfig, page: newPage });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    updateSearchConfig({
      rowsPerPage: newRowsPerPage,
      page: 0
    });
    fetchData(getEmployeesAttendance, filters, { ...searchConfig, rowsPerPage: newRowsPerPage, page: 0 });
  };

  const handleAdditionalFiltersApply = (filterData: any) => {
    updateMultipleFilters({
      employer: filterData.payload.employer_id_eq || '',
      employerLocation: filterData.payload.employer_location_id_eq || '',
      subContractor: filterData.payload.sub_contractor_id_eq || '',
      subContractorLocation: filterData.payload.sub_contractor_location_id_eq || ''
     });
    setSelectedAdditionalFilters(filterData.selectedFilters || {});
    setShowAdditionalFiltersModal(false);
  };

  const onClickAllCheckBox = () => {
    if (selectedAttendance.length !== employeesAttendance.length) {
      const allIds = employeesAttendance && employeesAttendance.map((each: any) => each.id)
      setSelectedAttendance(allIds)
    } else {
      setSelectedAttendance([])
    }
  }

  const onClickIndividualCheckBox = (id: any) => {
    if (selectedAttendance.includes(id)) {
      const updatedSelectedAttendance: any = selectedAttendance.filter((each: any) => each != id)
      setSelectedAttendance(updatedSelectedAttendance)
    } else {
      setSelectedAttendance([...selectedAttendance, id])
    }
  }
  const onClickBulkDelete = () => {
    setOpenBulkDeleteModal(true);
  };

  const onClickConfirmBulkDelete = () => {
    dispatch(bulkDeleteEmployeeAttendance(selectedAttendance));
  };

  useEffect(() => {
    if (bulkDeleteEmployeesAttendanceDetails.status === 'succeeded') {
      toast.success(`Attendance deleted successfully.`);
      setSelectedAttendance([]);
      dispatch(resetBulkDeleteHolidaysDetails());
      setOpenBulkDeleteModal(false);
      fetchData(getEmployeesAttendance, filters, searchConfig);
    } else if (bulkDeleteEmployeesAttendanceDetails.status === 'failed') {
      toast.error(ERROR_MESSAGES.DEFAULT);
    }
  }, [bulkDeleteEmployeesAttendanceDetails.status]);

  useEffect(() => {
    if (uploadHolidayDetails.status === 'succeeded') {
      if (uploadHolidayDetails.data.size === 0) {
        toast.success(`Holiday List Uploaded successfully.`);
        dispatch(resetUploadHolidayDetails());
        setOpenUploadModal(false);
        setUploadError(false);
        fetchData(getEmployeesAttendance, filters, searchConfig);
      } else {
        setUploadError(true);
      }
    } else if (uploadHolidayDetails.status === 'failed') {
      toast.error(ERROR_MESSAGES.DEFAULT);
    }
  }, [uploadHolidayDetails]);

  useEffect(() => {
    if (employeesAttendanceDetails.status === 'succeeded') {

    } else if (employeesAttendanceDetails.status === 'failed') {
      toast.error(ERROR_MESSAGES.DEFAULT);
    }
  }, [employeesAttendanceDetails.status]);

  const onClickExport = () => {
    const { per, page, ...payload } = buildEmployeeUploadQueryPayload(
      filters,
      searchConfig,
      { monthFieldName: 'wage_month_eq', searchFieldName: 'employee_code_cont' },
      {},
      'employee_code',
      'asc'
    );
    exportRecords(payload);
  };

  // Sort handlers
  const onClickSortCode = () => handleSort('employee_code');
  const onClickSortPresentDays = () => handleSort('present_days');
  const onClickSortWageMonth = () => handleSort('wage_month');

  return (
    <div style={{ height: '100%', backgroundColor: '#ffffff' }}>
      
      <Modal
        open={openBulkDeleteModal}
        onClose={() => setOpenBulkDeleteModal(false)}
      >
        <Box sx={style}>
          <Box sx={{ backgroundColor: '#E2E3F8', padding: '10px', px: '20px', borderRadius: '6px', boxShadow: '0px 6px 10px #CDD2D9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ font: 'normal normal normal 32px/40px Calibri' }}>Delete Employees</Typography>
            <IconButton
              onClick={() => setOpenBulkDeleteModal(false)}
            >
              <IoMdClose />
            </IconButton>
          </Box>
          <Box sx={{ padding: '20px', backgroundColor: '#ffffff' }}>
            <Box>
              <Typography variant='h5'>There are {selectedAttendance.length} record(s) selected for deleting.</Typography>
              <Typography mt={2}>Are you sure you want to delete all of them ?</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Button variant='outlined' color="error" onClick={() => setOpenBulkDeleteModal(false)}>No</Button>
              <Button variant='contained' onClick={onClickConfirmBulkDelete}>Yes</Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {loading ? <PageLoader>Loading...</PageLoader> :

        <div style={{ height: '100%' }}>
          <Box sx={{ paddingX: '20px', paddingY: '10px' }}>
            <div style={{ backgroundColor: '#E2E3F8', padding: '20px', borderRadius: '6px', boxShadow: '0px 6px 10px #CDD2D9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', marginTop: '10px' }}>
                <h5 style={{ font: 'normal normal normal 32px/40px Calibri' }}>Employee Attendance</h5>
                <div style={{ marginRight: '12px', display: 'flex', alignItems: 'center', width: '200px', justifyContent: 'space-between' }}>
                  {/* <Button onClick={onClickBackToDashboard} variant='contained'> Back To Dashboard</Button> */}

                  {
                    //   hasUserAccess(USER_PRIVILEGES.DELETE_EMPLOYEE_ATTENDANCE) &&
                    // <Button onClick={onClickBulkDelete} variant='contained' color='error' disabled={selectedAttendance && selectedAttendance.length === 0}> Bulk Delete</Button>
                  }
                  <button onClick={onClickExport} disabled={!filters.company || !filters.associateCompany} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: !filters.company || !filters.associateCompany ? '#707070' : '#ffffff', color: !filters.company || !filters.associateCompany ? '#ffffff' : '#000000', border: '1px solid #000000', width: '40px', height: '30px', borderRadius: '8px' }}> <FaDownload /> </button>
                </div>
              </div>
              <EmployeeUploadFilters
                filters={filters}
                searchConfig={searchConfig}
                companies={companies}
                associateCompanies={associateCompanies}
                locations={locations}
                onUpdateFilter={updateFilter}
                onUpdateSearchConfig={updateSearchConfig}
                onSearch={handleSearch}
                onClearSearch={handleClearSearch}
                showYearMonth={true}
                showAdditionalFilters={true}
                onAdditionalFiltersClick={() => {
                  if (filters.company && filters.associateCompany && filters.location) {
                    setShowAdditionalFiltersModal(true)
                  } else {
                    toast.error('Please select Company, Associate Company and Location to apply additional filters')
                  }
                }}
                searchLabel="Search (Employee Code)"
                searchPlaceholder="Search"
              />
            </div>
          </Box>

          {/* Additional Filters Modal */}
          {showAdditionalFiltersModal && (
            <AdditionalFiltersInputUploads
              data={{
                establishmentType: filters.establishmentType,
                company: filters.company,
                associate_company: filters.associateCompany,
                location: companyLocationMappingId
              }}
              selectedFilters={selectedAdditionalFilters}
              onSubmit={handleAdditionalFiltersApply}
              onCancel={() => setShowAdditionalFiltersModal(false)}
            />
          )}

            <Box sx={{paddingX: '20px', height: 'calc(100% - 215px)'}}>
              {
                employeesAttendance && employeesAttendance.length <= 0 ? 

                <Box sx={{height:'60vh', display:'flex', justifyContent:'center', alignItems:'center'}}>
                  <Typography variant='h5'>No Records Found</Typography>
                </Box>
                : 
                <>
                  <TableContainer sx={{ border: '1px solid #e6e6e6', marginTop: '10px', height: '90%', overflowY: 'scroll' }}>
                    <Table stickyHeader sx={{ minWidth: 650 }} aria-label="sticky table">
                      <TableHead sx={{ '.MuiTableCell-root': { backgroundColor: '#E7EEF7', fontWeight: '600' } }}>
                        <TableRow>
                          {/* <TableCell><Checkbox checked={(selectedAttendance && selectedAttendance.length) === (employeesAttendance && employeesAttendance.length)} onClick={onClickAllCheckBox} /></TableCell> */}
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'employee_code'} direction={searchConfig.sortType} onClick={onClickSortCode}>Employee Code</TableSortLabel></TableCell>
                            {filters.establishmentType == "CLRA" && <TableCell >Company Employer Sub-Contractor Code</TableCell>}
                          {/* <TableCell > <TableSortLabel active={searchConfig.activeSort === 'employeeName'} direction={searchConfig.sortType} onClick={onClickSortName}> Name</TableSortLabel></TableCell> */}
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'present_days'} direction={searchConfig.sortType} onClick={onClickSortPresentDays}> Present Days</TableSortLabel></TableCell>
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'wage_month'} direction={searchConfig.sortType} onClick={onClickSortWageMonth}> Wage Month</TableSortLabel></TableCell>
                            <TableCell >Office In TIME</TableCell>
                            <TableCell >Office Out Time</TableCell>
                            <TableCell >Interval In Time</TableCell>
                            <TableCell >Interval Out Time</TableCell>
                            <TableCell >Day 1</TableCell>
                            <TableCell >Day 2</TableCell>
                            <TableCell >Day 3</TableCell>
                            <TableCell >Day 4</TableCell>
                            <TableCell >Day 5</TableCell>
                            <TableCell >Day 6</TableCell>
                            <TableCell >Day 7</TableCell>
                            <TableCell >Day 8</TableCell>
                            <TableCell >Day 9</TableCell>
                            <TableCell >Day 10</TableCell>
                            <TableCell >Day 11</TableCell>
                            <TableCell >Day 12</TableCell>
                            <TableCell >Day 13</TableCell>
                            <TableCell >Day 14</TableCell>
                            <TableCell >Day 15</TableCell>
                            <TableCell >Day 16</TableCell>
                            <TableCell >Day 17</TableCell>
                            <TableCell >Day 18</TableCell>
                            <TableCell >Day 19</TableCell>
                            <TableCell >Day 20</TableCell>
                            <TableCell >Day 21</TableCell>
                            <TableCell >Day 22</TableCell>
                            <TableCell >Day 23</TableCell>
                            <TableCell >Day 24</TableCell>
                            <TableCell >Day 25</TableCell>
                            <TableCell >Day 26</TableCell>
                            <TableCell >Day 27</TableCell>
                            <TableCell >Day 28</TableCell>
                            <TableCell >Day 29</TableCell>
                            <TableCell >Day 30</TableCell>
                            <TableCell >Day 31</TableCell>
                            {/* <TableCell > Actions</TableCell> */}
                          </TableRow>
                        </TableHead>

                              <TableBody>
                              {employeesAttendance && employeesAttendance.map((each: any, index: number) => (
                                <TableRow
                                  key={each._id}
                                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >   
                                  {/* <TableCell><Checkbox checked={selectedAttendance.includes(each.id)} onClick={() => onClickIndividualCheckBox(each.id)} /></TableCell> */}
                                  <TableCell >{each.employee_code}</TableCell>
                                  {filters.establishmentType == "CLRA" && <TableCell >{(each.company_employer_sub_contractor || {}).company_employer_sub_contractor_code}</TableCell>}
                                  {/* <TableCell >{each.employeeName}</TableCell> */}
                                  <TableCell >{each.present_days}</TableCell>
                                  <TableCell >{each.wage_month}</TableCell>
                                  <TableCell >{(each.office_in_time)}</TableCell>
                                  <TableCell >{(each.office_out_time)}</TableCell>
                                  <TableCell >{(each.interval_in_time)}</TableCell>
                                  <TableCell >{(each.interval_out_time)}</TableCell>
                                  <TableCell >{each.day1}</TableCell>
                                  <TableCell >{each.day2}</TableCell>
                                  <TableCell >{each.day3}</TableCell>
                                  <TableCell >{each.day4}</TableCell>
                                  <TableCell >{each.day5}</TableCell>
                                  <TableCell >{each.day6}</TableCell>
                                  <TableCell >{each.day7}</TableCell>
                                  <TableCell >{each.day8}</TableCell>
                                  <TableCell >{each.day9}</TableCell>
                                  <TableCell >{each.day10}</TableCell>
                                  <TableCell >{each.day11}</TableCell>
                                  <TableCell >{each.day12}</TableCell>
                                  <TableCell >{each.day13}</TableCell>
                                  <TableCell >{each.day14}</TableCell>
                                  <TableCell >{each.day15}</TableCell>
                                  <TableCell >{each.day16}</TableCell>
                                  <TableCell >{each.day17}</TableCell>
                                  <TableCell >{each.day18}</TableCell>
                                  <TableCell >{each.day19}</TableCell>
                                  <TableCell >{each.day20}</TableCell>
                                  <TableCell >{each.day21}</TableCell>
                                  <TableCell >{each.day22}</TableCell>
                                  <TableCell >{each.day23}</TableCell>
                                  <TableCell >{each.day24}</TableCell>
                                  <TableCell >{each.day25}</TableCell>
                                  <TableCell >{each.day26}</TableCell>
                                  <TableCell >{each.day27}</TableCell>
                                  <TableCell >{each.day28}</TableCell>
                                  <TableCell >{each.day29}</TableCell>
                                  <TableCell >{each.day30}</TableCell>
                                  <TableCell >{each.day31}</TableCell>

                                  {/* <TableCell >
                                    <Box sx={{display:'flex', justifyContent:'space-between', width:'100px'}}>
                                      <Icon action={() => onclickEdit(each)} style={{color:'#039BE5'}} type="button" name={'pencil'} text={'Edit'}/>
                                      <Icon action={() => onclickDelete(each)} style={{color:'#EB1010'}} type="button" name={'trash'} text={'Delete'}/>
                                      <Icon action={() => onclickView(each)}  style={{color:'#00C853'}} type="button" name={'eye'} text={'View'}/>
                                    </Box>
                                  </TableCell> */}
                                </TableRow>
                              ))}
                              </TableBody>
                          </Table>
                  </TableContainer>
                  <TablePagination
                    sx={{
                      '.MuiTablePagination-toolbar': {
                        backgroundColor: '#EFEBFE',
                        height: '30px',
                        display: 'flex',
                        justifyContent: 'flex-end'
                      },
                      '.MuiTablePagination-displayedRows': {
                        margin: '0',
                      },
                      '.MuiTablePagination-selectLabel': {
                        margin: '0',
                      },
                      '.MuiTablePagination-spacer': {
                        display: 'none '
                      },
                      '.MuiTablePagination-input': {
                        marginRight: 'auto'
                      },
                    }}

                    labelRowsPerPage='Show'
                    labelDisplayedRows={(page) =>
                      `Showing ${page.from}-${page.to === -1 ? page.count : page.to} of ${page.count}`
                    }
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={employeesAttendanceCount}
                    rowsPerPage={searchConfig.rowsPerPage}
                    page={searchConfig.page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </>
            }
            </Box>
        </div>
      }
    </div>
  )
}

export default EmployeeAttendanceUpload